import React, { useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency } from '../../utils/formatters';

interface InlineEditFieldProps {
  value: number;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  editable?: boolean;
  variant?: 'positive' | 'negative' | 'neutral';
  width?: string;
  privacyMode?: boolean;
  formatFn?: (value: number, privacyMode?: boolean) => string;
}

export const InlineEditField: React.FC<InlineEditFieldProps> = ({
  value,
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  editable = true,
  variant = 'positive',
  width = 'w-24 md:w-28',
  privacyMode = false,
  formatFn = formatCurrency,
}) => {
  const { darkMode } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSave();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  const variantColors = {
    positive: darkMode ? 'text-green-400' : 'text-green-600',
    negative: darkMode ? 'text-red-400' : 'text-red-600',
    neutral: darkMode ? 'text-white' : 'text-slate-800',
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="number"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={onSave}
        className={`
          w-24 md:w-28 ml-auto px-2 py-1 rounded border
          ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-800'}
          focus:outline-none focus:ring-2 focus:ring-purple-500
          text-right font-bold block
        `}
      />
    );
  }

  return (
    <div className={`text-right ${width} ml-auto`}>
      <span
        onClick={() => editable && onStartEdit()}
        className={`
          font-bold ${variantColors[variant]}
          ${editable ? 'cursor-pointer hover:bg-white/10 rounded px-2 -mr-2' : ''}
          py-1 inline-block
        `}
      >
        {formatFn(value, privacyMode)}
      </span>
    </div>
  );
};

interface InlineEditTextProps {
  value: string;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  editable?: boolean;
  width?: string;
}

export const InlineEditText: React.FC<InlineEditTextProps> = ({
  value,
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  editable = true,
  width = 'flex-1',
}) => {
  const { darkMode } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSave();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={onSave}
        className={`
          ${width} px-2 py-1 rounded border
          ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-800'}
          focus:outline-none focus:ring-2 focus:ring-purple-500
        `}
      />
    );
  }

  return (
    <span
      onClick={() => editable && onStartEdit()}
      className={`
        ${darkMode ? 'text-slate-300' : 'text-slate-600'}
        ${editable ? 'cursor-pointer hover:bg-white/10 rounded px-2 py-1' : ''}
        ${width}
      `}
    >
      {value}
    </span>
  );
};
